import { useEffect, useMemo, useState } from "react"
import { UserCircle2, RefreshCw } from "lucide-react"
import { useAuth } from "../context/AuthContext"

type MicrosoftProfile = {
  displayName: string | null
  mail: string | null
  userPrincipalName: string | null
  jobTitle: string | null
  officeLocation: string | null
  mobilePhone: string | null
  businessPhones?: string[] | null
  preferredLanguage?: string | null
}

const GRAPH_ME_FIELDS =
  "displayName,mail,userPrincipalName,jobTitle,officeLocation,mobilePhone,businessPhones,preferredLanguage"

export default function MicrosoftProfileCard() {
  const { session } = useAuth() as any
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [profile, setProfile] = useState<MicrosoftProfile | null>(null)

  const token = session?.provider_token as string | undefined

  const email = useMemo(() => {
    if (!profile) return "—"
    return profile.mail || profile.userPrincipalName || "—"
  }, [profile])

  const fetchProfile = async () => {
    if (!token) {
      setError(
        "No se detecto un token de Microsoft. Inicia sesion con Microsoft 365 para ver tu perfil."
      )
      setProfile(null)
      return
    }

    try {
      setLoading(true)
      setError(null)

      const res = await fetch(
        `https://graph.microsoft.com/v1.0/me?$select=${encodeURIComponent(
          GRAPH_ME_FIELDS
        )}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )

      if (!res.ok) {
        const text = await res.text()
        throw new Error(text || "No se pudo obtener el perfil de Microsoft.")
      }

      const data = (await res.json()) as MicrosoftProfile
      setProfile(data)
    } catch (e: any) {
      setError(e?.message ?? "Error al cargar el perfil de Microsoft.")
      setProfile(null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchProfile()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  return (
    <div className="rounded-2xl border p-6 bg-white shadow-sm">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <UserCircle2 className="h-5 w-5 text-gray-700" />
          <h2 className="text-lg font-semibold">Perfil Microsoft 365</h2>
        </div>

        <button
          type="button"
          onClick={fetchProfile}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-60"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
          Actualizar
        </button>
      </div>

      {error && (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {!error && !profile && (
        <p className="mt-4 text-sm text-gray-500">
          {loading ? "Cargando perfil..." : "Perfil no disponible."}
        </p>
      )}

      {profile && (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-gray-500">Nombre</div>
            <div className="font-medium">{profile.displayName || "—"}</div>
          </div>
          <div>
            <div className="text-gray-500">Email</div>
            <div className="font-medium">{email}</div>
          </div>
          <div>
            <div className="text-gray-500">Cargo</div>
            <div className="font-medium">{profile.jobTitle || "—"}</div>
          </div>
          <div>
            <div className="text-gray-500">Oficina</div>
            <div className="font-medium">{profile.officeLocation || "—"}</div>
          </div>
          <div>
            <div className="text-gray-500">Telefono movil</div>
            <div className="font-medium">{profile.mobilePhone || "—"}</div>
          </div>
          <div>
            <div className="text-gray-500">Telefono laboral</div>
            <div className="font-medium">
              {profile.businessPhones && profile.businessPhones.length > 0
                ? profile.businessPhones.join(", ")
                : "—"}
            </div>
          </div>
          <div>
            <div className="text-gray-500">Idioma preferido</div>
            <div className="font-medium">{profile.preferredLanguage || "—"}</div>
          </div>
          <div>
            <div className="text-gray-500">UPN</div>
            <div className="font-medium">{profile.userPrincipalName || "—"}</div>
          </div>
        </div>
      )}
    </div>
  )
}
